import { FreeCamera, Scalar, Scene, Vector3 } from "@babylonjs/core";
import type { InputManager } from "../core/InputManager";
import { JUNGLE_CONFIG,type JungleObstacle } from "../environment/JungleGenerator";

export const CAMERA_PITCH_LIMIT = 75 * Math.PI / 180;
export const clampCameraPitch = (pitch: number): number => Scalar.Clamp(pitch, -CAMERA_PITCH_LIMIT, CAMERA_PITCH_LIMIT);
export function applyLookRotation(yaw: number, pitch: number, look: { yaw: number; pitch: number }): { yaw: number; pitch: number } { return { yaw: yaw + look.yaw, pitch: clampCameraPitch(pitch + look.pitch) }; }
export function horizontalCameraBasis(yaw: number): { forward: Vector3; right: Vector3 } {
  const forward = new Vector3(Math.sin(yaw), 0, Math.cos(yaw));
  return { forward, right: new Vector3(forward.z, 0, -forward.x) };
}

export class FirstPersonController {
  readonly camera: FreeCamera;
  private readonly radius = 0.42;
  private bobTime = 0;

  constructor(
    scene: Scene,
    private readonly input: InputManager,
    private readonly obstacles: readonly JungleObstacle[],
  ) {
    this.camera = new FreeCamera("player-camera", new Vector3(0, 1.65, 3), scene);
    this.camera.minZ = 0.05;
    this.camera.maxZ = 90;
    this.camera.fov = 1.05;
    scene.activeCamera = this.camera;
  }

  update(deltaSeconds: number): void {
    this.input.update();
    const look = this.input.consumeLook(deltaSeconds);
    const rotation = applyLookRotation(this.camera.rotation.y, this.camera.rotation.x, look);
    this.camera.rotation.y = rotation.yaw;
    this.camera.rotation.x = rotation.pitch;
    this.camera.rotation.z = 0;

    const { forward, right } = horizontalCameraBasis(this.camera.rotation.y);
    const desired = forward.scale(this.input.move.z).add(right.scale(this.input.move.x));
    if (desired.lengthSquared() > 0.01) {
      desired.normalize().scaleInPlace(4.25 * deltaSeconds);
      const next = this.camera.position.add(desired);
      next.x = Scalar.Clamp(next.x,-JUNGLE_CONFIG.playableHalfExtent,JUNGLE_CONFIG.playableHalfExtent);
      next.z = Scalar.Clamp(next.z,-JUNGLE_CONFIG.playableHalfExtent,JUNGLE_CONFIG.playableHalfExtent);
      if (!this.collides(next)) {
        this.camera.position.x = next.x;
        this.camera.position.z = next.z;
      } else {
        const slideX = new Vector3(next.x, this.camera.position.y, this.camera.position.z);
        const slideZ = new Vector3(this.camera.position.x, this.camera.position.y, next.z);
        if (!this.collides(slideX)) this.camera.position.x = slideX.x;
        if (!this.collides(slideZ)) this.camera.position.z = slideZ.z;
      }
      this.bobTime += deltaSeconds * 9;
      this.camera.position.y = 1.65 + Math.sin(this.bobTime) * 0.025;
    } else {
      this.camera.position.y = Scalar.Lerp(this.camera.position.y, 1.65, deltaSeconds * 6);
    }
  }

  private collides(position: Vector3): boolean {
    return this.obstacles.some((obstacle) => {
      const dx = position.x - obstacle.position.x;
      const dz = position.z - obstacle.position.z;
      const limit = this.radius + obstacle.radius;
      return dx * dx + dz * dz < limit * limit;
    });
  }
}
